import { createSelector } from "@reduxjs/toolkit";
import { skipToken } from "@reduxjs/toolkit/query";
import type { IRootState } from "../app/store";
import type { ArticleColoringSettings } from "../SettingPage/articleColoringApi";
import { articleColoringApi } from "../SettingPage/articleColoringApi";
import { openapi, type PostMultiplacerArticlesByCompCodesApiArg } from "../../openapi";
import { selectViewerCompCodes } from "./articleSelectors";

const EMPTY_FILTERS: ArticleColoringSettings["articleColorings"] = [];
export const selectArticleColorings = (state: IRootState) =>
  articleColoringApi.endpoints.getMultiplacerProjectArticleColoring.select({ projectNo: state.design.projectNo })(state)
    .data?.articleColorings ?? EMPTY_FILTERS;
export const selectActiveArticleColoring = createSelector(
  [selectArticleColorings, (state: IRootState) => state.design.activeArticleColoringId],
  (filters, id) => filters.find(filter => filter.id === id) ?? null,
);
export const selectArticleQueryArgs = createSelector(
  [(state: IRootState) => state.design.projectNo, selectActiveArticleColoring, selectViewerCompCodes],
  (projectNo, filter, compCodes): PostMultiplacerArticlesByCompCodesApiArg | null =>
    projectNo && filter && compCodes.length ? { body: { projectNo, compCodes } } : null,
);
export const selectArticleQuery = (state: IRootState) =>
  openapi.endpoints.postMultiplacerArticlesByCompCodes.select(selectArticleQueryArgs(state) ?? skipToken)(state);

export const selectArticleColorsByCompCode = createSelector(
  [selectActiveArticleColoring, selectArticleQuery],
  (filter, query) => {
    const colors = new Map<string, string>();
    if (!filter) return colors;
    const values = new Map(filter.values.map(entry => [entry.value, entry.color]));
    for (const article of query.data?.articles ?? []) {
      const value = article.properties?.[filter.propertyKey];
      const color = value == null ? undefined : values.get(value);
      if (color) colors.set(article.compCode, color);
    }
    return colors;
  },
);

export const selectArticleFallbackColor = createSelector(
  [selectActiveArticleColoring],
  (filter) => filter?.fallbackColor,
);
